import type { OperationKey } from '@foodlista/operations'
import type { OperationRun } from '@foodlista/trigger-vue'
import type { OperationItem } from '../types'
import { itemMatchesScope } from './matchScope'
import { readOperationContext } from './read-operation-context'

export interface ScopedOperationScope extends Record<string, unknown> {
  restaurantSlug: string
}

export type ScopeMatcher<Scope extends ScopedOperationScope> = (
  item: OperationItem,
  scope: Scope,
) => boolean

export function createOutputParser<Output, Result>(
  isOutput: (value: unknown) => value is Output,
  select: (output: Output) => Result,
  fallback: Result,
): (run: OperationRun) => Result {
  return (run) => {
    if (isOutput(run.output)) {
      return select(run.output)
    }

    return fallback
  }
}

/**
 * Rebuilds a partial scope from a persisted context. Goes through
 * `readOperationContext`, so a context this client cannot read yields only the
 * restaurant slug rather than fields it might misinterpret.
 */
export function scopeFieldsFromContext(input: {
  type: string
  contextVersion: number | null
  context: unknown
  restaurantSlug: string
  keys: readonly string[]
}): Record<string, unknown> {
  const context = readOperationContext(input)
  const fields: Record<string, unknown> = {}

  for (const key of input.keys) {
    if (key === 'restaurantSlug') {
      fields.restaurantSlug = input.restaurantSlug
      continue
    }

    const value = context?.[key]
    if (value !== undefined) {
      fields[key] = value
    }
  }

  return fields
}

export function scopePayloadKeys<Scope extends ScopedOperationScope>(
  scopePayload: (scope: Scope) => Record<string, unknown>,
): string[] {
  const template = scopePayload({ restaurantSlug: '' } as Scope)
  return ['restaurantSlug', ...Object.keys(template)]
}

export function createScopeMatcher<Scope extends ScopedOperationScope>(
  key: OperationKey,
  scopePayload: (scope: Scope) => Record<string, unknown>,
): ScopeMatcher<Scope> {
  return (item, scope) => itemMatchesScope(item, key, {
    restaurantSlug: scope.restaurantSlug,
    ...scopePayload(scope),
  })
}

export function buildScopedEnqueueInput<
  Input,
  Scope extends ScopedOperationScope,
>(
  input: Input,
  scope: Scope,
  scopePayload: (scope: Scope) => Record<string, unknown>,
  idempotencyKey: string,
) {
  return {
    ...input,
    ...scopePayload(scope),
    restaurantSlug: scope.restaurantSlug,
    idempotencyKey,
  }
}
